/**
 * storeAutoUrl.ts — маркер «адрес магазина по платформе посетителя»
 * (WEB-1906 / WEB-828, решение владельца 05.09, вариант б).
 *
 * ЗАЧЕМ. Концовка «редирект» у генерации ИИ-воронки заканчивается обычной
 * кнопкой-ссылкой (`button_link`). Куда она ведёт, модель знать не может и
 * выдумывать не должна (весь смысл WEB-828): трафик льют и на iOS, и на
 * Android, а адреса магазинов лежат в настройках проекта. Поэтому генерация
 * ставит в поле адреса СЛУЖЕБНЫЙ МАРКЕР, а настоящий адрес выбирает рантайм в
 * момент показа страницы.
 *
 * ДВА КОНЦА (WEB-1844). Ту же константу объявляет бэк —
 * `backend/src/ai-funnels/block-schema/store-auto-url.ts`. Разъедутся молча,
 * поэтому равенство держит сторож `storeAutoUrl.twoEnds.web1906.guard.test.ts`
 * (читает файл бэка и сверяет байт-в-байт), а второй копии литерала на фронте
 * он же не даёт завестись.
 *
 * ПРАВИЛО ЗДЕСЬ НЕ ЖИВЁТ — И ЭТО ГЛАВНОЕ (WEB-1910, решение владельца Q116
 * 06.09). Куда вести посетителя, решает ОДНА функция на весь фронт —
 * `resolveStoreUrl` в `openAppOrStore.ts`; её зеркало на бэке —
 * `pickStoreUrlForPlatform`, и обе стороны гоняют одну письменную таблицу
 * случаев `backend/src/projects/utils/store-url-rule.cases.json`.
 *
 * До WEB-1910 здесь стояло СВОЁ второе правило — «своего адреса нет, соседний
 * заполнен → ведём на соседний» — вторым вызовом `resolveStoreUrl` с
 * «платформа неизвестна». Оно было верным по существу (гейт генерации на бэке
 * пропускает концовку «редирект» при ХОТЯ БЫ ОДНОЙ заполненной ссылке:
 * `REDIRECT_ENDING_NO_STORE_URL` поднимается, только когда пусты обе, —
 * funnel-generation.service.ts; без него Android-посетитель проекта с одним
 * лишь iOS-адресом получал бы финальный экран БЕЗ кнопки), но было решением
 * ОРКЕСТРАТОРА и жило отдельной копией. Владелец сделал его общим правилом
 * продукта, и оно переехало внутрь `resolveStoreUrl`. Здесь остался один
 * вызов; второй копии не заводить — сторож
 * `storeUrlRule.twoEnds.web1910.guard.test.ts` гоняет через эту функцию всю
 * таблицу и краснеет, если её ответы разойдутся с общим правилом.
 *
 * Заполненных адресов нет вовсе → `null`: кнопка не рисуется, выдуманных
 * адресов не бывает ни при каком раскладе (п.3 приёмки WEB-828).
 */
import { resolveStoreUrl } from './openAppOrStore';

/**
 * Маркер в поле `url` блока `button_link`: «адрес подставит рантайм».
 * Схема `w2a` намеренно не похожа ни на что настоящее — это не адрес, по
 * которому куда-то ходят, а признак. Значение обязано совпадать с бэком.
 */
export const STORE_AUTO_URL = 'w2a://store';

/** Адреса магазинов проекта (WEB-256: приходят в публичной выдаче). */
export interface StoreAutoTargets {
  ios?: string | null;
  android?: string | null;
}

/**
 * Ровно этот маркер и ничего похожего: `w2a://evil` и `w2a://store/` —
 * обычные (негодные) адреса, а не признак.
 */
export function isStoreAutoUrl(value: unknown): boolean {
  return value === STORE_AUTO_URL;
}

/**
 * Адрес, на который ведёт маркер у конкретного посетителя. Ответ целиком
 * принадлежит общему правилу `resolveStoreUrl` (WEB-1910): свой магазин
 * платформы, иначе имеющийся соседний, иначе `null`.
 *
 * `null` означает «вести некуда»: вызывающий обязан НЕ рисовать кнопку, а не
 * подставлять выдуманный адрес (п.3 приёмки WEB-828).
 *
 * Функция остаётся ради ПЕРЕВОДА ИМЁН: у публичной выдачи воронки адреса
 * зовутся `ios`/`android` (WEB-256), у общего правила —
 * `iosStoreUrl`/`androidStoreUrl` (плюс пара ссылок с меткой перехода, которых
 * у концовки «редирект» нет). Здесь только этот перевод, ни одного решения о
 * выборе магазина.
 */
export function resolveStoreAutoUrl(
  targets: StoreAutoTargets,
  userAgent?: string | null,
): string | null {
  return resolveStoreUrl(userAgent, {
    iosStoreUrl: targets.ios,
    androidStoreUrl: targets.android,
  });
}
